import { createApi } from '@reduxjs/toolkit/query/react';
import { createBackendBaseQuery, transformApiError, withCountryHeader } from '../../utils/createAuthBaseQuery';
import { API_URLS } from '../../constants/apiUrl';

export interface MotorInvestigationSolutionResponse {
  motorNumber?: number;
  voltage?: string[];
  power?: string[];
  frequency?: string[];
  compressorType?: string[];
  distributionCenter?: string[];
}

export interface FetchMotorInvestigationSolutionTicketParams {
  oldMotorNumber?: number;
  machineName?: string;
  serialNumber?: string;
  regionId?: number;
  motorSolutionLogId?: number;
  isDirectTicketCreationFlow?: boolean;
  isSerialNumberSearched?: boolean;
  isSerialNumberSearchSuccessful?: boolean;
}

export interface FetchMotorInvestigationSolutionParams {
  serialNumber?: string;
  motorNumber?: number;
  motorSolutionLogId?: number;
}

// Mirrors the backend CreateMotorInvestigationTicketRequest fields consumed by
// the CheckAvailableSolution endpoint. Property names bind case-insensitively to
// the request object (e.g. oldMotorNumber -> OldMotorNumber, machineName -> MachineName).
export interface CheckAvailableSolutionParams {
  oldMotorNumber: number;
  machineName?: string;
  serialNumber?: string;
  regionId?: number;
  motorSolutionLogId?: number;
  isDirectTicketCreationFlow?: boolean;
  isSerialNumberSearched?: boolean;
  isSerialNumberSearchSuccessful?: boolean;
}

export interface MotorInvestigationDuplicateResponse {
  ticketId: number;
  childTicket: string;
  isTicketAvailable: boolean;
  isEngineeringTicketCreated: boolean;
  isMarketingTicketCreated: boolean;
}

const BASE_URL = process.env.REACT_APP_MOTER_SERVICE_API_BASE_URL as string;

if (!BASE_URL) {
  throw new Error('REACT_APP_MOTER_SERVICE_API_BASE_URL environment variable is not set');
}

// Create the base query using the reusable factory
const baseQuery = createBackendBaseQuery(BASE_URL);

// Attach the user's country header to the Solution/Search, CheckAvailableSolution
// and DuplicateCheck requests; the other endpoints in this slice pass through unchanged.
const baseQueryWithCountry = withCountryHeader(
  baseQuery,
  (url) =>
    url.includes(API_URLS.MOTOR_SERVICE.SOLUTION_SEARCH) ||
    url.includes(API_URLS.MOTOR_SERVICE.CHECK_AVAILABLE_SOLUTION) ||
    url.includes(API_URLS.MOTOR_INVESTIGATION.DUPLICATE_CHECK),
  'motorInvestigationSolutionApi'
);

export const motorInvestigationSolutionApiSlice = createApi({
  reducerPath: 'motorInvestigationSolutionApi',
  baseQuery: baseQueryWithCountry,
  tagTypes: ['MotorInvestigationSolution'],
  endpoints(builders) {
    return {
      fetchMotorInvestigationsSolution: builders.query<
        MotorInvestigationSolutionResponse,
        FetchMotorInvestigationSolutionParams
      >({
        query: (params) => {
          let queryString = '';
          if (params) {
            const usp = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
              if (value !== undefined && value !== null && value !== '') {
                usp.append(key, String(value));
              }
            });
            queryString = usp.toString() ? `?${usp.toString()}` : '';
          }
          return {
            url: `${API_URLS.MOTOR_SERVICE.SOLUTION_SEARCH}${queryString}`,
            method: 'GET',
          };
        },
        providesTags: ['MotorInvestigationSolution'],
        transformErrorResponse: transformApiError,
      }),
      checkAvailableSolution: builders.query<
        boolean,
        CheckAvailableSolutionParams
      >({
        query: (params) => {
          let queryString = '';
          if (params) {
            const usp = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
              if (value !== undefined && value !== null && value !== '') {
                usp.append(key, String(value));
              }
            });
            queryString = usp.toString() ? `?${usp.toString()}` : '';
          }
          return {
            url: `${API_URLS.MOTOR_SERVICE.CHECK_AVAILABLE_SOLUTION}${queryString}`,
            method: 'GET',
          };
        },
        providesTags: ['MotorInvestigationSolution'],
        transformErrorResponse: transformApiError,
      }),
      fetchMotorInvestigationDuplicate: builders.query<
        MotorInvestigationDuplicateResponse,
        FetchMotorInvestigationSolutionTicketParams
      >({
        query: (params) => {
          let queryString = '';
          if (params) {
            const usp = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
              if (value !== undefined && value !== null && value !== '') {
                usp.append(key, String(value));
              }
            });
            queryString = usp.toString() ? `?${usp.toString()}` : '';
          }
          return {
            url: `${API_URLS.MOTOR_INVESTIGATION.DUPLICATE_CHECK}${queryString}`,
            method: 'GET',
          };
        },
        providesTags: ['MotorInvestigationSolution'],
        transformErrorResponse: transformApiError,
      }),
    };
  },
});

export const {
  useFetchMotorInvestigationsSolutionQuery,
  useLazyFetchMotorInvestigationsSolutionQuery,
  useLazyFetchMotorInvestigationDuplicateQuery,
  useLazyCheckAvailableSolutionQuery,
} = motorInvestigationSolutionApiSlice;